import { isSameDay } from 'date-fns';
import { useRef } from 'react';

import { ScenarioHooks, useDebounce } from '../../../../../hooks/index';
import { ScenarioType } from '../../../../../types/scenario-modeling.types';
import { toBackendDateFormat } from '../../../../../utils/to-backend-date-format.util';
import { toNumber } from '../../../../../utils/to-number.util';
import { useScenarioCapTableStore } from '../../../cap-table.store';
import { valuationFormSchema } from '../../scenario-details.validation';

export type UseLiquidityCreationProps = {
  scenarioId: string;
  valuation:
    | {
        accelerateVesting?: boolean;
        date?: Date | undefined;
        preMoneyValuation?: number | undefined;
        type?: ScenarioType | undefined;
      }
    | undefined;
};

type NormalizedValuation = {
  accelerateVesting?: boolean;
  date?: Date;
  preMoneyValuation?: number;
};

const normalizePreMoneyValuation = (value: unknown) => {
  if (value === null || value === undefined || value === '') return undefined;
  return toNumber(value as null | number | string | undefined);
};

const normalizeValuation = (
  value: UseLiquidityCreationProps['valuation'],
): NormalizedValuation => ({
  preMoneyValuation: normalizePreMoneyValuation(value?.preMoneyValuation),
  date: value?.date ? new Date(value.date) : undefined,
  accelerateVesting: value?.accelerateVesting,
});

const isSameValuation = (a: NormalizedValuation, b: NormalizedValuation) =>
  a.preMoneyValuation === b.preMoneyValuation &&
  Boolean(a.accelerateVesting) === Boolean(b.accelerateVesting) &&
  isSameDay(a.date || new Date(0), b.date || new Date(0));

export const useLiquidityCreation = ({ scenarioId, valuation }: UseLiquidityCreationProps) => {
  const { update, isPending } = ScenarioHooks.useUpdate();
  const scheduleUpdate = useScenarioCapTableStore(({ scheduleUpdate }) => scheduleUpdate);
  const { scenario } = ScenarioHooks.useScenario({ scenarioId });

  // Same echo protection as useFundraisingCreation: diff against SERVER state so form resets
  // fed by scenario refetches can never fire a PATCH (which wipes the generated cap table).
  const serverValuationRef = useRef<NormalizedValuation | null>(null);
  serverValuationRef.current = scenario
    ? {
        preMoneyValuation: normalizePreMoneyValuation(scenario.preMoneyValuation),
        date: scenario.closingDate ? new Date(scenario.closingDate) : undefined,
        accelerateVesting: scenario.accelerateVesting,
      }
    : null;

  const lastSentValuationRef = useRef<NormalizedValuation | null>(null);

  const { debounceCallback: debounceValuationCallback } = useDebounce<typeof valuation>({
    debounce: 500,
    callback: () => {
      const { success } = valuationFormSchema.safeParse(valuation);
      if (!success) return;

      const serverValuation = serverValuationRef.current;
      if (!serverValuation) return;

      const normalized = normalizeValuation(valuation);

      // An entirely empty valuation means the form has not hydrated yet — never a user edit.
      if (normalized.preMoneyValuation === undefined && normalized.date === undefined) return;

      if (isSameValuation(serverValuation, normalized)) return;
      if (lastSentValuationRef.current && isSameValuation(lastSentValuationRef.current, normalized))
        return;

      scheduleUpdate(scenarioId);
      update({
        scenarioId,
        data: {
          preMoneyValuation: normalized.preMoneyValuation,
          closingDate: normalized.date ? toBackendDateFormat(normalized.date) : undefined,
          accelerateVesting: normalized.accelerateVesting,
        },
      });
      lastSentValuationRef.current = normalized;
    },
  });
  return { debounceValuationCallback, isPending };
};
